import { useEffect, useState } from 'react';
import { useOrgEmployeeRequestsApi } from '@shared/services';
import type { EmployeeRequest } from '@shared/models/requestTypes';
import { type Request, RequestStatus } from '@features/requests/types';
import { toEmployeeRequest, toLegacyRequests } from '../utils';
import { sortByDeadlineAsc } from '../utils';

/** Maximum number of drafts returned by the hook */
export const DRAFTS_LIMIT = 5;

/**
 * Result of the useDrafts hook
 */
export interface UseDraftsResult {
    /** Latest drafts sorted by deadline (closest first) */
    drafts: Request[];
    isLoading: boolean;
    error: string | null;
}

/**
 * Custom hook that fetches the open requests for the given orgEmployee
 * and returns only the drafts, sorted by deadline from closest to
 * furthest, limited to the most relevant ones.
 *
 * @param orgEmployeeId - The orgEmployeeId used for the API call
 * @param limit - Maximum number of drafts to return (default 5)
 */
export function useDrafts(
    orgEmployeeId: string | null,
    limit: number = DRAFTS_LIMIT,
): UseDraftsResult {
    const { getOpen: getOpenRequests } = useOrgEmployeeRequestsApi();
    const [drafts, setDrafts] = useState<Request[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect((): void => {
        const fetchDrafts = async (): Promise<void> => {
            // If no orgEmployeeId, don't try to fetch
            if (!orgEmployeeId || orgEmployeeId.trim() === '') {
                return;
            }

            setIsLoading(true);
            setError(null);
            try {
                const response = await getOpenRequests(orgEmployeeId);
                const employeeRequests: EmployeeRequest[] =
                    response.elements.map(toEmployeeRequest);
                const legacyRequests = toLegacyRequests(employeeRequests);
                const draftRequests = legacyRequests.filter(
                    (request): boolean =>
                        request.status === RequestStatus.DRAFT,
                );
                setDrafts(sortByDeadlineAsc(draftRequests).slice(0, limit));
            } catch (err) {
                // Authentication errors are handled by ProtectedRoute
                // Only show other errors
                const errorMessage =
                    err instanceof Error ? err.message : String(err);
                if (
                    !errorMessage.includes('Not authenticated') &&
                    !errorMessage.includes('Unauthorized') &&
                    !errorMessage.includes('login_required')
                ) {
                    setError('Kunne ikke hente utkast. Prøv igjen senere.');
                }
            } finally {
                setIsLoading(false);
            }
        };

        void fetchDrafts();
    }, [getOpenRequests, orgEmployeeId, limit]);

    return { drafts, isLoading, error };
}
